/**
 * Anant Feelings — Direct Dil Se.
 * 3 personas (Anushka/Anaya/Arjun) · 200 AT for 10-min text+voice chat ·
 * Video loop background · 9th-min warning · auto-kill at 600s.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Mic, Send, X, Sparkles, Clock, MessageCircle, Volume2, Heart, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import BuyATBalanceModal from '../../components/BuyATBalanceModal';

const BACKEND = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

// Mood detection — analyze last AI reply text to emit a warm "aura" around the
// persona avatar. Empathetic tone → soft green pulse; tough-love tone → amber pulse.
// Heuristic keyword lists cover Hindi (Roman + Devanagari) + English.
const EMPATHETIC_HINTS = [
  'samajh', 'समझ', 'feel', 'understand', 'hug', 'believe', 'proud', 'gentle',
  'saath', 'sath', 'साथ', 'chinta mat', 'चिंता', 'ok hai', 'okay hai', 'sab thik',
  'acha', 'achha', 'sundar', 'beautiful', 'love', 'pyaar', 'प्यार', 'trust',
  'safe', 'here for you', 'main hoon', 'मैं हूँ', 'breathe', 'saans',
  'sorry', 'gently', 'gentle', '❤', '💛', '🤗',
];
const TOUGH_HINTS = [
  'wake up', 'stop', 'must', 'reality', 'excuse', 'responsibility', 'grow up',
  'chodo', 'छोड़ो', 'hosh', 'होश', 'kadwa', 'कड़वा', 'sach', 'सच',
  'discipline', 'action', 'karna hoga', 'करना होगा', 'time nahi', 'no time',
  'enough', 'bas', 'बस', 'change', 'बदल', 'push',
];
function detectMood(text) {
  if (!text) return 'neutral';
  const t = text.toLowerCase();
  let emp = 0, tough = 0;
  for (const w of EMPATHETIC_HINTS) { if (t.includes(w)) emp++; }
  for (const w of TOUGH_HINTS) { if (t.includes(w)) tough++; }
  if (tough > emp && tough >= 1) return 'tough';
  if (emp > 0) return 'empathetic';
  return 'neutral';
}

const AnantFeelings = () => {
  const [personas, setPersonas] = useState([]);
  const [session, setSession] = useState(null);
  const [persona, setPersona] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const [warned, setWarned] = useState(false);
  const [showBuyAT, setShowBuyAT] = useState(false);
  const [listening, setListening] = useState(false);
  const audioRef = useRef(null);
  const recogRef = useRef(null);
  const navigate = useNavigate();

  // Real-time mood detection based on latest AI reply — drives the "mood ring"
  // aura around the persona avatar. Recomputes only when messages change.
  const mood = useMemo(() => {
    const lastAi = [...messages].reverse().find(m => m.role === 'ai');
    return detectMood(lastAi?.text || '');
  }, [messages]);
  const moodClass = mood === 'empathetic' ? 'mood-empathetic' : mood === 'tough' ? 'mood-tough' : '';

  useEffect(() => {
    api.get('/anant-feelings/personas').then(r => setPersonas(r.data.personas || [])).catch(() => {});
  }, []);

  // Timer
  useEffect(() => {
    if (!session) return;
    const tick = setInterval(() => {
      setRemaining(prev => {
        const next = Math.max(0, prev - 1);
        if (next === 60 && !warned) {
          setWarned(true);
          toast.warning('⚠️ 1 minute remaining. Approve next 200 AT to continue or session will end.', { duration: 10000 });
        }
        if (next === 0) {
          endSession('expired');
          clearInterval(tick);
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [session, warned]);

  const start = async (p) => {
    setBusy(true);
    try {
      const r = await api.post('/anant-feelings/session/start', { persona_id: p.id });
      setSession({ id: r.data.session_id, ...r.data });
      setPersona(p);
      setRemaining(r.data.duration_sec || 600);
      setMessages([{ role: 'ai', text: `Hi, मैं ${p.name} हूँ। ${p.tagline}. Aap kya share karna chahte ho?` }]);
      toast.success(`Connected with ${p.name} ✨ · 200 AT deducted`);
    } catch (e) {
      const detail = e.response?.data?.detail || 'Could not start session';
      toast.error(detail);
      if (detail.toLowerCase().includes('insufficient')) setShowBuyAT(true);
    } finally { setBusy(false); }
  };

  const endSession = async (reason = 'user') => {
    if (!session) return;
    try { await api.post('/anant-feelings/session/end', { session_id: session.id }); } catch (_) {}
    try { audioRef.current?.pause(); } catch (_) {}
    if (reason === 'expired') toast.info('Session ended (10 min over). Khoob jiya. Phir milte hain. 🙏', { duration: 6000 });
    setSession(null); setPersona(null); setMessages([]); setRemaining(0); setWarned(false);
  };

  const send = async () => {
    if (!input.trim() || !session || busy) return;
    const userText = input.trim();
    setInput('');
    setMessages(m => [...m, { role: 'user', text: userText }]);
    setBusy(true);
    try {
      const r = await api.post('/anant-feelings/session/message', { session_id: session.id, text: userText });
      setMessages(m => [...m, { role: 'ai', text: r.data.reply, tts: r.data.tts_url }]);
      setRemaining(r.data.remaining_sec ?? remaining);
      // Auto-play voice reply
      if (r.data.tts_url) {
        try {
          const blob = await api.get(r.data.tts_url, { responseType: 'blob' });
          const url = URL.createObjectURL(blob.data);
          try { audioRef.current?.pause(); } catch (_) {}
          const a = new Audio(url); a.volume = 1.0; audioRef.current = a; a.play().catch(() => {});
        } catch (_) {}
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reply failed');
    } finally { setBusy(false); }
  };

  const toggleListen = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error('Voice input not supported on this browser'); return; }
    if (listening) { try { recogRef.current?.stop(); } catch (_) {} setListening(false); return; }
    const r = new SR();
    r.lang = 'hi-IN'; r.continuous = false; r.interimResults = false;
    let appended = 0;
    r.onresult = (e) => {
      // Only append new final segments to avoid duplication when Chrome re-fires results.
      let fresh = '';
      for (let i = appended; i < e.results.length; i++) {
        if (e.results[i].isFinal) fresh += e.results[i][0].transcript + ' ';
      }
      appended = e.results.length;
      const txt = fresh.trim();
      if (txt) setInput(prev => (prev ? prev + ' ' : '') + txt);
    };
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    recogRef.current = r;
    setListening(true);
    try { r.start(); } catch (_) { setListening(false); }
  };

  const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  // ============ Persona picker view ============
  if (!session) {
    return (
      <div className="min-h-screen pb-16 px-4 bg-gradient-to-br from-slate-950 via-rose-950 to-slate-950 text-white" data-testid="feelings-picker">
        <div className="max-w-5xl mx-auto pt-6">
          <button onClick={() => navigate(-1)} data-testid="feelings-back-btn"
            className="mb-4 inline-flex items-center gap-1.5 text-rose-200/70 hover:text-amber-300 text-sm font-medium">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="text-center mb-8">
            <Heart className="w-10 h-10 text-rose-300 mx-auto mb-2" />
            <h1 className="text-4xl sm:text-5xl font-extrabold bg-gradient-to-r from-rose-300 via-pink-300 to-amber-300 bg-clip-text text-transparent mb-2">
              Anant Feelings · Direct Dil Se
            </h1>
            <p className="text-rose-100/80 max-w-2xl mx-auto text-sm sm:text-base">
              Choose a companion. 10 minutes of real text + voice chat. <span className="text-amber-300 font-bold">200 AT</span> · 7-level commission split to your Webchain · server-side 10-min timer.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            {personas.map(p => (
              <button
                key={p.id}
                onClick={() => start(p)}
                disabled={busy}
                data-testid={`persona-${p.id}`}
                className="text-left rounded-2xl overflow-hidden border-2 border-rose-400/30 bg-slate-900 hover:border-amber-400 hover:scale-[1.02] transition disabled:opacity-50"
              >
                <div className={`aspect-square relative overflow-hidden bg-gradient-to-br ${p.gradient || 'from-rose-500 via-pink-500 to-purple-600'}`}>
                  {p.avatar_url ? (
                    <img
                      src={p.avatar_url}
                      alt={p.name}
                      className="w-full h-full object-cover anant-breathe"
                      style={{ filter: 'saturate(1.15) brightness(1.05)' }}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : null}
                  {/* Subtle blink overlay — makes avatar feel "alive" */}
                  {p.avatar_url ? <div className="anant-blink-overlay" style={{ zIndex: 1 }} /> : null}
                  {/* Fallback letter — always rendered underneath so broken images degrade gracefully */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ zIndex: 0 }}>
                    <div className="text-[8rem] font-black text-white/90 drop-shadow-2xl select-none anant-float">
                      {p.name?.[0]}
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                  </div>
                </div>
                <div className="p-4">
                  <div className="text-xs uppercase tracking-wider text-rose-400">{p.gender}</div>
                  <h3 className="text-2xl font-extrabold text-amber-200 mt-1">{p.name}</h3>
                  <p className="text-sm text-rose-100/70 mt-1 line-clamp-2">{p.tagline}</p>
                  <div className="mt-3 text-xs font-bold text-black bg-amber-500 inline-block px-3 py-1.5 rounded-md">
                    Connect · 200 AT
                  </div>
                </div>
              </button>
            ))}
          </div>

          <div className="mt-8 text-center">
            <button onClick={() => setShowBuyAT(true)} data-testid="feelings-buy-at"
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-black font-bold hover:scale-105 transition inline-flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> Buy AT Balance
            </button>
          </div>

          <div className="mt-10 text-center text-xs text-rose-200/50">
            ⚠️ AI companion — for emotional support only. If you're in crisis, please call iCall +91-9152987821 or Vandrevala 1860-2662-345 (free 24×7).
          </div>
        </div>
        <BuyATBalanceModal open={showBuyAT} onClose={() => setShowBuyAT(false)} />
      </div>
    );
  }

  // ============ Active session view ============
  return (
    <div className="min-h-screen pt-3 pb-4 px-3 bg-slate-950 text-white flex flex-col" data-testid="feelings-session">
      <div className="max-w-3xl w-full mx-auto flex-1 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            {persona?.avatar_url ? (
              <img
                src={persona.avatar_url}
                alt={persona?.name}
                className="w-10 h-10 rounded-full object-cover border-2 border-amber-400"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            ) : (
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-white border-2 border-amber-400 bg-gradient-to-br ${persona?.gradient || 'from-rose-500 to-purple-600'}`}>
                {persona?.name?.[0]}
              </div>
            )}
            <div>
              <div className="font-extrabold text-amber-200">{persona?.name}</div>
              <div className="text-[10px] text-slate-400">{persona?.tagline?.slice(0, 40)}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className={`text-xs font-mono px-2 py-1 rounded-md ${remaining < 60 ? 'bg-rose-500 text-white animate-pulse' : 'bg-amber-500/20 text-amber-300'}`} data-testid="feelings-timer">
              <Clock className="w-3 h-3 inline mr-1" /> {mmss(remaining)}
            </div>
            <button onClick={() => endSession('user')} data-testid="feelings-end-btn" className="px-2 py-1 rounded-md bg-rose-500 text-white text-xs font-bold inline-flex items-center gap-1"><X className="w-3 h-3" /> End</button>
          </div>
        </div>

        {/* Video/Avatar area — wrapped with dynamic mood class for real-time emotional aura */}
        <div className={`relative rounded-2xl overflow-hidden border border-amber-500/30 mb-3 aspect-video bg-gradient-to-br ${persona?.gradient || 'from-rose-500 via-pink-500 to-purple-600'} ${moodClass}`} data-testid="feelings-avatar-frame" data-mood={mood}>
          {persona?.video_loop_url ? (
            <video src={persona.video_loop_url} autoPlay muted loop playsInline className="w-full h-full object-cover" />
          ) : persona?.avatar_url ? (
            <>
              <img
                src={persona.avatar_url}
                alt={persona?.name}
                className="w-full h-full object-cover anant-breathe"
                style={{ filter: busy ? 'brightness(0.9) saturate(1.2)' : 'brightness(1) saturate(1.1)' }}
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
              <div className="anant-blink-overlay" style={{ zIndex: 1 }} />
            </>
          ) : (
            <div className="absolute inset-0 overflow-hidden">
              {/* Soft radial glow layers */}
              <div className="absolute inset-0 opacity-50"
                style={{ background: 'radial-gradient(circle at 28% 32%, rgba(255,255,255,0.40), transparent 55%), radial-gradient(circle at 72% 68%, rgba(255,210,160,0.35), transparent 55%)' }} />
              {/* Slow orbital glow */}
              <div className="absolute left-1/2 top-1/2 w-40 h-40 -translate-x-1/2 -translate-y-1/2 anant-orbit">
                <div className="w-12 h-12 rounded-full bg-white/30 blur-xl" />
              </div>
              {/* Expanding rings */}
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 rounded-full border border-white/40 anant-ring" />
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 rounded-full border border-white/30 anant-ring" style={{ animationDelay: '1.7s' }} />
              {/* Floating particles */}
              {[
                { l: '18%', d: '20px', delay: '0s' },
                { l: '38%', d: '-10px', delay: '1.5s' },
                { l: '58%', d: '15px', delay: '0.8s' },
                { l: '78%', d: '-25px', delay: '2.4s' },
                { l: '88%', d: '5px', delay: '3.2s' },
              ].map((p, i) => (
                <span key={i} className="absolute bottom-0 w-1.5 h-1.5 rounded-full bg-white/70 anant-particle"
                  style={{ left: p.l, animationDelay: p.delay, '--dx': p.d }} />
              ))}
              {/* Big floating letter */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className={`text-[12rem] font-black text-white/90 drop-shadow-2xl select-none anant-float ${busy ? 'anant-pulse-soft' : ''}`}>
                  {persona?.name?.[0]}
                </div>
              </div>
              <div className="absolute bottom-0 inset-x-0 h-1/3 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
            </div>
          )}
          {busy && (
            <div className="absolute bottom-3 left-3 px-3 py-1.5 rounded-full bg-black/70 text-amber-300 text-xs font-bold inline-flex items-center gap-2">
              <Volume2 className="w-3 h-3 anant-pulse-soft" /> {persona?.name} is typing…
            </div>
          )}
          <div className="absolute top-3 right-3 px-2 py-1 rounded-md bg-rose-500 text-white text-[10px] font-bold inline-flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-white anant-pulse-soft" /> LIVE · BETA
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto space-y-2 mb-3 max-h-[40vh]" data-testid="feelings-messages">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${m.role === 'user' ? 'bg-amber-500 text-black' : 'bg-slate-800 text-slate-100'}`}>
                {m.text}
              </div>
            </div>
          ))}
        </div>

        {/* Input */}
        <div className="flex items-center gap-2">
          <button onClick={toggleListen} data-testid="feelings-mic-btn"
            className={`w-12 h-12 rounded-full flex items-center justify-center border ${listening ? 'bg-rose-500 text-white animate-pulse' : 'bg-slate-800 text-amber-300 border-slate-700 hover:bg-slate-700'}`}>
            <Mic className="w-5 h-5" />
          </button>
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && send()}
            placeholder="Apni baat likhiye..."
            data-testid="feelings-input"
            className="flex-1 px-4 py-3 rounded-full bg-slate-900 border border-slate-700 text-slate-100 focus:border-amber-400 focus:outline-none"
            disabled={busy}
          />
          <button onClick={send} disabled={!input.trim() || busy} data-testid="feelings-send-btn"
            className="w-12 h-12 rounded-full bg-amber-500 hover:bg-amber-400 text-black flex items-center justify-center disabled:opacity-50">
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnantFeelings;
